import config from "../config";
import { formatDateDMY, formatDateTimeDMY, parseDateValue } from "./dates";

function esc(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

function rupees(n) {
  const v = Number(n);
  if (!Number.isFinite(v)) return "Rs.—";
  const abs = Math.abs(v);
  const body = Number.isInteger(abs) ? String(abs) : abs.toFixed(2).replace(/\.?0+$/, "");
  return `${v < 0 ? "Rs.-" : "Rs."}${body}`;
}

function parseDate(value) {
  return parseDateValue(value);
}

function invoiceDateLabel(value) {
  const d = parseDate(value);
  if (!d) return "";
  return formatDateDMY(d);
}

function visitDateLabel(value) {
  const d = parseDate(value);
  if (!d) return "";
  return formatDateDMY(d);
}

function slotRange(slot) {
  const s = String(slot || "").trim();
  if (!s) return "";
  if (s.includes("-")) return s.replace(/\s*-\s*/, " - ");
  const m = s.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return s;
  const h = Number(m[1]);
  const min = m[2];
  const endH = String((h + 1) % 24).padStart(2, "0");
  return `${String(h).padStart(2, "0")}:${min} - ${endH}:${min}`;
}

function lineAmount(lines, pred) {
  const row = (lines || []).find(pred);
  return row ? Number(row.amount) || 0 : 0;
}

function fileSafe(name) {
  return String(name || "vaccine")
    .replace(/[^\w]+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 48) || "vaccine";
}

async function logoDataUrl() {
  const src = config.LOGO_URL || "/logo-cropped.png";
  try {
    const res = await fetch(src);
    if (!res.ok) throw new Error("logo");
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result || ""));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return `${typeof window !== "undefined" ? window.location.origin : ""}${src}`;
  }
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2500);
}

export function buildVaccineReportHtml(record = {}, user = {}, logoSrc = "") {
  const breakdown = Array.isArray(record.price_breakdown) ? record.price_breakdown : [];
  const isGst = (l) => {
    const code = (l.code || "").toLowerCase();
    const label = String(l.label || "").toUpperCase();
    return ["gst", "cgst", "sgst", "igst", "tax"].includes(code)
      || label.includes("GST") || label.includes("CGST") || label.includes("SGST") || label.includes("IGST");
  };
  const isAdmin = (l) => /admin/i.test(l.code || "") || /admin/i.test(l.label || "");
  const isBase = (l) => !isGst(l) && !isAdmin(l) && (l.calc || "fixed") === "fixed";

  const vaccineAmt = Number(
    record.vaccine_amount
    ?? (breakdown.some(isBase) ? lineAmount(breakdown, isBase) : null)
    ?? record.indicative_price
    ?? record.final_amount
  ) || 0;
  const adminAmt = lineAmount(breakdown, isAdmin);
  const taxLines = breakdown.filter(isGst);
  const gstAmt = taxLines.reduce((sum, l) => sum + (Number(l.amount) || 0), 0);
  const discount = Number(record.discount_amount) || 0;
  const total = Number(record.final_amount ?? record.final_price ?? (vaccineAmt + adminAmt + gstAmt - discount)) || 0;

  const customerName = record.customer_name || user.full_name || user.name || "";
  const customerPhone = record.customer_phone || user.phone || "";
  const beneficiary = record.beneficiary_name || "";
  const forLabel = record.for_label || (beneficiary && customerName && beneficiary.trim().toLowerCase() === String(customerName).trim().toLowerCase()
    ? `Self (${beneficiary})`
    : (beneficiary || "Self"));
  const badges = (record.badges || ["Cold Chain Maintained", "Same-Day Booking"]).filter(Boolean);
  const created = record.created_at || record.applied_at || "";
  const ymd = String(created).slice(0, 10).replace(/-/g, "");
  const tail = String(record.booking_id || record.id || "").replace(/[^a-zA-Z0-9]/g, "").slice(-4).toUpperCase() || "0000";
  const invoiceNo = record.invoice_no || `INV-${ymd || "00000000"}-${tail}`;
  const bookingNo = record.booking_no || `BMJ-${ymd || "00000000"}-${tail}`;
  const adminWhen = record.vaccine_administered_at || record.applied_at
    ? formatDateTimeDMY(record.vaccine_administered_at || record.applied_at)
    : [visitDateLabel(record.preferred_date), slotRange(record.preferred_slot)].filter(Boolean).join("<br/>");
  const logo = logoSrc || `${typeof window !== "undefined" ? window.location.origin : ""}${config.LOGO_URL || "/logo-cropped.png"}`;

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>${esc(record.vaccine_name || "Vaccine")} report — BookMyJab</title>
  <style>
    @page { size: A4; margin: 16mm; }
    * { box-sizing: border-box; }
    body { font-family: Arial, Helvetica, sans-serif; color: #1f1a2e; margin: 0; padding: 28px 36px; background: #fff; }
    .top { display: flex; justify-content: space-between; align-items: flex-start; gap: 24px; }
    .brand img { height: 52px; width: auto; display: block; }
    .tagline { margin-top: 4px; font-size: 11px; color: #f37721; font-style: italic; }
    .meta { text-align: right; }
    .meta h1 { margin: 0 0 8px; color: #f37721; font-size: 22px; letter-spacing: 0.04em; }
    .meta p { margin: 2px 0; font-size: 12px; color: #4b5563; }
    hr { border: none; border-top: 1px solid #e5e7eb; margin: 18px 0 22px; }
    .bill { font-size: 11px; color: #9ca3af; letter-spacing: 0.12em; font-weight: 700; margin-bottom: 6px; }
    .who { font-size: 16px; font-weight: 800; color: #111827; margin: 0 0 4px; }
    .who-sub { margin: 0; font-size: 13px; color: #4b5563; line-height: 1.45; }
    table { width: 100%; border-collapse: collapse; margin-top: 22px; }
    thead th { background: #f37721; color: #fff; font-size: 11px; letter-spacing: 0.04em; text-align: left; padding: 10px 12px; }
    tbody td { padding: 14px 12px; vertical-align: top; font-size: 13px; border-bottom: 1px solid #ececec; }
    .amt { text-align: right; white-space: nowrap; font-weight: 700; }
    .orange { color: #f37721; font-weight: 700; }
    .badge { color: #4b5563; font-size: 12px; line-height: 1.35; }
    .totals { width: 320px; margin-left: auto; margin-top: 18px; }
    .totals div { display: flex; justify-content: space-between; padding: 6px 0; font-size: 13px; color: #4b5563; }
    .totals .grand { background: #fff4ec; color: #f37721; font-weight: 800; padding: 10px 12px; margin-top: 6px; }
    .totals .grand span:last-child { font-size: 16px; }
  </style>
</head>
<body>
  <div class="top">
    <div class="brand">
      <img src="${esc(logo)}" alt="BookMyJab" />
      <div class="tagline">Uncompromising Care. Seamlessly Yours.</div>
    </div>
    <div class="meta">
      <h1>TAX INVOICE</h1>
      <p>Invoice No: ${esc(invoiceNo)}</p>
      <p>Booking No: ${esc(bookingNo)}</p>
      <p>Date: ${esc(invoiceDateLabel(created || record.preferred_date))}</p>
    </div>
  </div>
  <hr />
  <div class="bill">BILL TO</div>
  <p class="who">${esc(customerName || beneficiary || "Customer")}</p>
  <p class="who-sub">${esc(customerPhone)}<br/>${esc(record.address || "")}</p>
  <table>
    <thead>
      <tr>
        <th>VACCINE</th>
        <th>BATCH NO</th>
        <th>FOR</th>
        <th>BADGE</th>
        <th>DATE &amp; TIME OF ADMINISTRATION</th>
        <th>QTY</th>
        <th style="text-align:right">AMOUNT</th>
      </tr>
    </thead>
    <tbody>
      <tr>
        <td><strong>${esc(record.vaccine_name || "Vaccine")}</strong></td>
        <td>${esc(record.batch_number || "—")}</td>
        <td>${esc(forLabel)}</td>
        <td class="badge">${badges.map(esc).join("<br/>")}</td>
        <td class="orange">${adminWhen || "—"}</td>
        <td>1</td>
        <td class="amt">${rupees(vaccineAmt)}</td>
      </tr>
    </tbody>
  </table>
  <div class="totals">
    <div><span>Subtotal</span><span>${rupees(vaccineAmt)}</span></div>
    ${adminAmt ? `<div><span>Administration Charges</span><span>${rupees(adminAmt)}</span></div>` : ""}
    ${discount ? `<div><span>Discount</span><span>${rupees(-Math.abs(discount))}</span></div>` : ""}
    ${taxLines.length
      ? taxLines.map((l) => `<div><span>${esc(l.label || "Tax")}</span><span>${rupees(l.amount)}</span></div>`).join("")
      : (gstAmt ? `<div><span>Tax</span><span>${rupees(gstAmt)}</span></div>` : "")}
    <div class="grand"><span>Total Amount</span><span>${rupees(total)}</span></div>
  </div>
</body>
</html>`;
}

export async function downloadVaccineReport(record, user) {
  const logo = await logoDataUrl();
  const html = buildVaccineReportHtml(record, user, logo);
  const blob = new Blob(["\uFEFF", html], { type: "application/octet-stream" });
  const filename = `BookMyJab-${fileSafe(record.vaccine_name)}-report.html`;
  triggerDownload(blob, filename);
}

export function openVaccineReport(record, user) {
  return downloadVaccineReport(record, user);
}
